import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Award,
  Plus,
  Settings,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Edit,
  Trash2,
  Loader2,
  Save,
  Users,
  Bell,
  Calendar,
  Upload,
  FileText,
  Image,
  X,
  Eye,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import StatTile from '../components/StatTile';

const API = process.env.REACT_APP_BACKEND_URL;
import { resolveFileUrl } from '../lib/fileUtils';

// Status color mapping
const getStatusBgClass = (color) => {
  switch (color) {
    case 'green': return 'bg-green-100 text-green-800 border-green-300';
    case 'yellow': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'orange': return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'red': return 'bg-red-100 text-red-800 border-red-300';
    default: return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

const getStatusCellClass = (color) => {
  switch (color) {
    case 'green': return 'bg-green-50';
    case 'yellow': return 'bg-yellow-50';
    case 'orange': return 'bg-orange-50';
    case 'red': return 'bg-red-50';
    default: return 'bg-gray-50';
  }
};

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'good': return <CheckCircle className="w-4 h-4 text-green-600" />;
    case 'warning': return <Clock className="w-4 h-4 text-yellow-600" />;
    case 'critical': return <AlertTriangle className="w-4 h-4 text-orange-600" />;
    case 'expired': return <XCircle className="w-4 h-4 text-red-600" />;
    case 'missing': return <XCircle className="w-4 h-4 text-red-600" />;
    case 'missing_data': return <AlertCircle className="w-4 h-4 text-slate-400" />;
    default: return null;
  }
};

export default function Certifications() {
  const { isAdmin, user } = useAuth();
  // Cert-manager access: admin/super_user/manager always allowed; any other
  // user can be granted via the per-user permissions toggle from the Team
  // Member edit form so admins can delegate this page without granting full
  // admin rights.
  const canManageCerts = (
    isAdmin() ||
    user?.role === 'manager' ||
    !!user?.permissions?.manage_certifications
  );
  const [activeTab, setActiveTab] = useState('matrix');
  const [loading, setLoading] = useState(true);
  
  // Data state
  const [certTypes, setCertTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [stats, setStats] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [allTeamMembers, setAllTeamMembers] = useState([]);
  
  // Manager settings
  const [managers, setManagers] = useState([]);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [reminderSettings, setReminderSettings] = useState({
    days_before_60: true,
    days_before_30: true,
    days_before_7: true,
    notify_employee: true,
    notify_managers: true
  });
  
  // Dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [selectedCert, setSelectedCert] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [certForm, setCertForm] = useState({
    employee_id: '',
    certification_type_id: '',
    date_completed: '',
    date_expires: '',
    notes: '',
    files: []
  });
  
  const [typeForm, setTypeForm] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [matrixRes, statsRes, expiringRes, managersRes, reminderRes, teamRes] = await Promise.all([
        axios.get(`${API}/api/certifications/matrix`),
        axios.get(`${API}/api/certifications/stats`),
        axios.get(`${API}/api/certifications/expiring?days=60`),
        axios.get(`${API}/api/certifications/settings/managers`),
        axios.get(`${API}/api/certifications/settings/reminders`),
        axios.get(`${API}/api/team`)
      ]);
      
      setCertTypes(matrixRes.data.certification_types);
      setEmployees(matrixRes.data.employees);
      setMatrix(matrixRes.data.matrix);
      setStats(statsRes.data);
      setExpiring(expiringRes.data);
      setSelectedManagers(managersRes.data.manager_ids || []);
      setReminderSettings(reminderRes.data);
      setAllTeamMembers(teamRes.data);
      setManagers(teamRes.data.filter(m => m.role === 'admin' || m.role === 'super_user' || m.role === 'manager'));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Try to seed defaults if no certification types exist
      if (error.response?.status === 404 || certTypes.length === 0) {
        await seedDefaults();
      }
    } finally {
      setLoading(false);
    }
  };

  const seedDefaults = async () => {
    try {
      await axios.post(`${API}/api/certifications/seed-defaults`);
      toast.success('Default certification types created');
      fetchData();
    } catch (error) {
      // Ignore if already seeded
    }
  };

  const handleCellClick = (employeeId, employeeName, certTypeId, certTypeName, certData) => {
    setSelectedCert({
      employeeId,
      employeeName,
      certTypeId,
      certTypeName,
      ...certData
    });
    setCertForm({
      employee_id: employeeId,
      certification_type_id: certTypeId,
      date_completed: certData?.date_completed || '',
      date_expires: certData?.date_expires || '',
      notes: certData?.notes || '',
      files: certData?.files || []
    });
    setShowEditDialog(true);
  };

  const handleSaveCertification = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/certifications/employees`, certForm);
      toast.success('Certification updated');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAddType = () => {
    setSelectedType(null);
    setTypeForm({ name: '', description: '' });
    setShowTypeDialog(true);
  };

  const handleEditType = (type) => {
    setSelectedType(type);
    setTypeForm({ name: type.name, description: type.description || '' });
    setShowTypeDialog(true);
  };

  const handleSaveType = async () => {
    setSaving(true);
    try {
      if (selectedType) {
        await axios.put(`${API}/api/certifications/types/${selectedType.id}`, typeForm);
        toast.success('Certification type updated');
      } else {
        await axios.post(`${API}/api/certifications/types`, typeForm);
        toast.success('Certification type created');
      }
      setShowTypeDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteType = async (type) => {
    setSelectedType(type);
    setShowDeleteDialog(true);
  };

  const confirmDeleteType = async () => {
    try {
      await axios.delete(`${API}/api/certifications/types/${selectedType.id}`);
      toast.success('Certification type removed');
      setShowDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSaveManagers = async () => {
    try {
      await axios.post(`${API}/api/certifications/settings/managers`, {
        manager_ids: selectedManagers
      });
      toast.success('Managers updated');
    } catch (error) {
      toast.error('Failed to save managers');
    }
  };

  const handleSaveReminderSettings = async () => {
    try {
      await axios.post(`${API}/api/certifications/settings/reminders`, reminderSettings);
      toast.success('Reminder settings updated');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image (JPG, PNG, WEBP, HEIC) or PDF');
      return;
    }

    // Check file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/api/certifications/employees/${certForm.employee_id}/${certForm.certification_type_id}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      // Add to local state
      setCertForm(prev => ({
        ...prev,
        files: [...(prev.files || []), response.data.file]
      }));
      
      toast.success('File uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleDeleteFile = async (fileUrl) => {
    try {
      await axios.delete(
        `${API}/api/certifications/employees/${certForm.employee_id}/${certForm.certification_type_id}/file`,
        { data: { file_url: fileUrl } }
      );
      
      // Remove from local state
      setCertForm(prev => ({
        ...prev,
        files: prev.files.filter(f => f.url !== fileUrl)
      }));
      
      toast.success('File deleted');
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handlePreviewFile = (file) => {
    setPreviewFile(file);
    setShowFilePreview(true);
  };

  const handleToggleExclude = async (memberId, currentlyExcluded) => {
    try {
      await axios.post(`${API}/api/certifications/exclude/${memberId}?exclude=${!currentlyExcluded}`);
      toast.success(currentlyExcluded ? 'Member included in certifications' : 'Member excluded from certifications');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const toggleManager = (managerId) => {
    setSelectedManagers(prev => 
      prev.includes(managerId)
        ? prev.filter(id => id !== managerId)
        : [...prev, managerId]
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'MM/dd/yyyy');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="certifications-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight flex items-center gap-3">
            <Award className="w-8 h-8 text-amber-500" />
            Employee Certifications
          </h1>
          <p className="text-slate-600 mt-1">Track and manage team member certifications</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-2xl font-bold text-green-700">{stats.valid}</span>
              </div>
              <p className="text-sm text-green-600 mt-1">Valid</p>
            </CardContent>
          </Card>
          
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-600" />
                <span className="text-2xl font-bold text-yellow-700">{stats.expiring_60}</span>
              </div>
              <p className="text-sm text-yellow-600 mt-1">60 Days</p>
            </CardContent>
          </Card>
          
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                <span className="text-2xl font-bold text-orange-700">{stats.expiring_30}</span>
              </div>
              <p className="text-sm text-orange-600 mt-1">30 Days</p>
            </CardContent>
          </Card>
          
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                <span className="text-2xl font-bold text-red-700">{stats.expired}</span>
              </div>
              <p className="text-sm text-red-600 mt-1">Expired</p>
            </CardContent>
          </Card>
          
          <Card className="border-gray-200 bg-gray-50">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-gray-500" />
                <span className="text-2xl font-bold text-gray-700">{stats.missing}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">No Data</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="matrix" data-testid="tab-matrix">
            <Award className="w-4 h-4 mr-2" />
            Certification Matrix
          </TabsTrigger>
          <TabsTrigger value="expiring" data-testid="tab-expiring">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Expiring Soon ({expiring.length})
          </TabsTrigger>
          {canManageCerts && (
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          )}
        </TabsList>

        {/* Matrix Tab */}
        <TabsContent value="matrix" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-['Barlow_Condensed']">Certification Matrix</CardTitle>
                <div className="flex items-center gap-2 text-xs">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500"></span> Valid</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-500"></span> 60 Days</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-500"></span> 30 Days</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500"></span> Expired/None</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white z-10 min-w-[150px]">Employee</TableHead>
                    {certTypes.map(type => (
                      <TableHead key={type.id} className="text-center min-w-[120px] text-xs">
                        {type.name}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matrix.map(row => (
                    <TableRow key={row.employee_id}>
                      <TableCell className="sticky left-0 bg-white z-10 font-medium">
                        {row.employee_name}
                      </TableCell>
                      {certTypes.map(type => {
                        const cert = row.certifications[type.id];
                        const statusColor = cert?.status_info?.color || 'red';
                        return (
                          <TableCell 
                            key={type.id}
                            className={`text-center cursor-pointer hover:opacity-80 transition-opacity ${getStatusCellClass(statusColor)}`}
                            onClick={() => handleCellClick(
                              row.employee_id, 
                              row.employee_name, 
                              type.id, 
                              type.name, 
                              cert
                            )}
                            data-testid={`cert-cell-${row.employee_id}-${type.id}`}
                          >
                            <div className="flex flex-col items-center gap-1">
                              <StatusIcon status={cert?.status_info?.status} />
                              <span className="text-xs">
                                {cert?.status_info?.status === 'missing_data'
                                  ? <span className="text-slate-500 italic">Not set</span>
                                  : cert?.date_expires ? formatDate(cert.date_expires)
                                  : cert?.date_completed ? formatDate(cert.date_completed)
                                  : '-'}
                              </span>
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expiring Tab */}
        <TabsContent value="expiring" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Certifications Expiring Within 60 Days
              </CardTitle>
            </CardHeader>
            <CardContent>
              {expiring.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No certifications expiring within 60 days</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Certification</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expiring.map(cert => (
                      <TableRow key={cert.id}>
                        <TableCell className="font-medium">{cert.employee_name}</TableCell>
                        <TableCell>{cert.certification_name}</TableCell>
                        <TableCell>{formatDate(cert.date_completed)}</TableCell>
                        <TableCell>{formatDate(cert.date_expires)}</TableCell>
                        <TableCell>
                          <Badge className={getStatusBgClass(cert.status_info?.color)}>
                            <StatusIcon status={cert.status_info?.status} />
                            <span className="ml-1">{cert.status_info?.label}</span>
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        {canManageCerts && (
          <TabsContent value="settings" className="mt-4">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Certification Types */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                      <Award className="w-5 h-5 text-amber-500" />
                      Certification Types
                    </CardTitle>
                    <Button size="sm" onClick={handleAddType} data-testid="add-cert-type-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Type
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {certTypes.map(type => (
                      <div 
                        key={type.id} 
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{type.name}</p>
                          {type.description && (
                            <p className="text-xs text-slate-500">{type.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEditType(type)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {isAdmin() && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteType(type)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Manager Assignment — admin/super_user only */}
              {isAdmin() && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    Certification Managers
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Select managers who will receive expiration reminders
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {managers.map(manager => (
                      <div key={manager.id} className="flex items-center space-x-3">
                        <Checkbox
                          id={`manager-${manager.id}`}
                          checked={selectedManagers.includes(manager.id)}
                          onCheckedChange={() => toggleManager(manager.id)}
                        />
                        <label
                          htmlFor={`manager-${manager.id}`}
                          className="text-sm font-medium leading-none cursor-pointer"
                        >
                          {manager.full_name}
                          <span className="text-xs text-slate-500 ml-2">({manager.role})</span>
                        </label>
                      </div>
                    ))}
                  </div>
                  <Button 
                    className="mt-4 w-full"
                    onClick={handleSaveManagers}
                    data-testid="save-managers-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Managers
                  </Button>
                </CardContent>
              </Card>
              )}

              {/* Reminder Settings */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                    <Bell className="w-5 h-5 text-purple-500" />
                    Reminder Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium mb-3">Reminder Timing</h4>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <Switch
                            id="days-60"
                            checked={reminderSettings.days_before_60}
                            onCheckedChange={(v) => setReminderSettings(p => ({...p, days_before_60: v}))}
                          />
                          <Label htmlFor="days-60">60 days before expiration</Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Switch
                            id="days-30"
                            checked={reminderSettings.days_before_30}
                            onCheckedChange={(v) => setReminderSettings(p => ({...p, days_before_30: v}))}
                          />
                          <Label htmlFor="days-30">30 days before expiration</Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Switch
                            id="days-7"
                            checked={reminderSettings.days_before_7}
                            onCheckedChange={(v) => setReminderSettings(p => ({...p, days_before_7: v}))}
                          />
                          <Label htmlFor="days-7">7 days before expiration</Label>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium mb-3">Notification Recipients</h4>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <Switch
                            id="notify-employee"
                            checked={reminderSettings.notify_employee}
                            onCheckedChange={(v) => setReminderSettings(p => ({...p, notify_employee: v}))}
                          />
                          <Label htmlFor="notify-employee">Notify the employee</Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Switch
                            id="notify-managers"
                            checked={reminderSettings.notify_managers}
                            onCheckedChange={(v) => setReminderSettings(p => ({...p, notify_managers: v}))}
                          />
                          <Label htmlFor="notify-managers">Notify selected managers</Label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button 
                    className="mt-6"
                    onClick={handleSaveReminderSettings}
                    data-testid="save-reminder-settings-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Reminder Settings
                  </Button>
                </CardContent>
              </Card>

              {/* Exclude Members from Tracking */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                    <Users className="w-5 h-5 text-gray-500" />
                    Exclude Team Members
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Select team members to exclude from certification tracking (e.g., office-only staff)
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-3">
                    {allTeamMembers.map(member => (
                      <div key={member.id} className="flex items-center space-x-3 p-2 rounded bg-slate-50">
                        <Checkbox
                          id={`exclude-${member.id}`}
                          checked={member.exclude_from_certifications === true}
                          onCheckedChange={() => handleToggleExclude(member.id, member.exclude_from_certifications)}
                        />
                        <label
                          htmlFor={`exclude-${member.id}`}
                          className="text-sm leading-none cursor-pointer flex-1"
                        >
                          {member.full_name}
                          {member.exclude_from_certifications && (
                            <Badge variant="outline" className="ml-2 text-xs">Excluded</Badge>
                          )}
                        </label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Edit Certification Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedCert?.employeeName} - {selectedCert?.certTypeName}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date_completed">Date Completed</Label>
                <Input
                  id="date_completed"
                  type="date"
                  value={certForm.date_completed}
                  onChange={(e) => setCertForm(p => ({...p, date_completed: e.target.value}))}
                  data-testid="cert-date-completed"
                />
              </div>
              <div>
                <Label htmlFor="date_expires">Expiration Date</Label>
                <Input
                  id="date_expires"
                  type="date"
                  value={certForm.date_expires}
                  onChange={(e) => setCertForm(p => ({...p, date_expires: e.target.value}))}
                  data-testid="cert-date-expires"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={certForm.notes}
                onChange={(e) => setCertForm(p => ({...p, notes: e.target.value}))}
                placeholder="Optional notes about this certification..."
                rows={2}
                data-testid="cert-notes"
              />
            </div>

            {/* File Uploads Section */}
            <div>
              <Label className="mb-2 block">Certification Documents</Label>
              <div className="border rounded-lg p-3 bg-slate-50">
                {/* Existing Files */}
                {certForm.files?.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {certForm.files.map((file, idx) => (
                      <div 
                        key={file.id || idx} 
                        className="flex items-center justify-between p-2 bg-white rounded border"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {file.content_type?.includes('pdf') ? (
                            <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
                          ) : (
                            <Image className="w-5 h-5 text-blue-500 flex-shrink-0" />
                          )}
                          <span className="text-sm truncate">{file.filename || 'Document'}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreviewFile(file)}
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(resolveFileUrl(file), '_blank')}
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteFile(file.url)}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading}
                    data-testid="cert-file-upload"
                  />
                  <div className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 rounded-lg hover:border-amber-400 hover:bg-amber-50 transition-colors">
                    {uploading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                    ) : (
                      <Upload className="w-5 h-5 text-slate-400" />
                    )}
                    <span className="text-sm text-slate-600">
                      {uploading ? 'Uploading...' : 'Upload photo or PDF'}
                    </span>
                  </div>
                </label>
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Upload certification cards, certificates, or proof documents
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCertification} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Certification Type Dialog */}
      <Dialog open={showTypeDialog} onOpenChange={setShowTypeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedType ? 'Edit Certification Type' : 'Add Certification Type'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="type_name">Name *</Label>
              <Input
                id="type_name"
                value={typeForm.name}
                onChange={(e) => setTypeForm(p => ({...p, name: e.target.value}))}
                placeholder="e.g., OSHA-30"
                data-testid="cert-type-name"
              />
            </div>
            
            <div>
              <Label htmlFor="type_description">Description</Label>
              <Textarea
                id="type_description"
                value={typeForm.description}
                onChange={(e) => setTypeForm(p => ({...p, description: e.target.value}))}
                placeholder="Optional description..."
                rows={2}
                data-testid="cert-type-description"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTypeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveType} disabled={saving || !typeForm.name}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {selectedType ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Certification Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedType?.name}"? This will deactivate the certification type but preserve existing records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteType} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* File Preview Dialog */}
      <Dialog open={showFilePreview} onOpenChange={setShowFilePreview}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewFile?.content_type?.includes('pdf') ? (
                <FileText className="w-5 h-5 text-red-500" />
              ) : (
                <Image className="w-5 h-5 text-blue-500" />
              )}
              {previewFile?.filename || 'Document Preview'}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 flex justify-center overflow-auto">
            {previewFile?.content_type?.includes('pdf') ? (
              <iframe
                src={resolveFileUrl(previewFile)}
                className="w-full h-[70vh] border rounded"
                title="PDF Preview"
              />
            ) : (
              <img
                src={resolveFileUrl(previewFile)}
                alt="Certificate"
                className="max-w-full max-h-[70vh] object-contain rounded"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => window.open(resolveFileUrl(previewFile), '_blank')}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button onClick={() => setShowFilePreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
